import {
  faArrowDownToLine as DownloadIcon,
  faEye
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import type { SearchableSelectOption } from '@/components/SearchableSelect';
import TableWidget from '@/components/TableWidget';
import PERMISSIONS from '@/constants/auth.constants';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import ReportsDialog from '@/features/ReportsDialog';
import ReportTitleEditDialog from '@/features/ReportTitleEditDialog';
import SearchBar, { getSearchableSelectOptions } from '@/features/SearchBar';
import SocMonthlySummaryDialog from '@/features/SocMonthlySummaryDialog';
import useConfirm from '@/hooks/useConfirm';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import { organizationIdNameAtom } from '@/pages/Organizations/Organizations.store';
import { checkPermissions } from '@/services/permissions';

import { REPORT_QUICK_FILTERS, REPORT_TYPE_ID } from './Reports.constants';
import useReports from './Reports.hooks';
import useStyles from './Reports.styles';
import type { ReportType } from './Reports.types';
import createColumns from './Reports.utils';

const Reports = () => {
  const [organizationIdNames] = useAtom(organizationIdNameAtom);
  const {
    deleteReport,
    mdrReports,
    vmpReports,
    edrReports,
    executiveReports,
    reports,
    user,
    selectedOrganization,
    reportsFilters,
    setReportsFilters,
    filteredReports,
    allReports,
    organizations,
    orgHasEdrProducts,
    orgHasEdrReports,
    orgHasVmpProducts,
    orgHasVmpReports
  } = useReports();

  const [open, setOpen] = useState(false);
  const [socDialogOpen, setSocDialogOpen] = useState(false);
  const [openTitleEdit, setOpenTitleEdit] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportType>();

  const userIsInternal = user?.internal;

  const hasSomeEdrReports = useMemo(() => {
    return allReports.some(
      (report) => report.report_type_id === REPORT_TYPE_ID.EDR
    );
  }, [allReports]);

  const hasSomeVmpReports = useMemo(() => {
    return allReports.some(
      (report) => report.report_type_id === REPORT_TYPE_ID.VMP
    );
  }, [allReports]);

  const showEdr = useMemo(() => {
    return (
      (orgHasEdrProducts &&
        orgHasEdrReports &&
        checkPermissions([PERMISSIONS.EDR_READ], user, selectedOrganization)) ||
      (selectedOrganization.label === 'all' && hasSomeEdrReports)
    );
  }, [orgHasEdrProducts, orgHasEdrReports, selectedOrganization, user]);

  const showVmp = useMemo(() => {
    return (
      (orgHasVmpReports &&
        checkPermissions([PERMISSIONS.VMP_READ], user, selectedOrganization)) ||
      (selectedOrganization.label === 'all' && hasSomeVmpReports) ||
      orgHasVmpProducts
    );
  }, [orgHasVmpProducts, orgHasVmpReports, selectedOrganization, user]);

  const availableReportTypes = useMemo(() => {
    // DEFAULT = executive and mdr reports
    if (showEdr && showVmp) {
      return 'DEFAULT_AND_EDR_AND_VMP';
    }

    if (showEdr || showVmp) {
      return 'DEFAULT_AND_EDR_OR_VMP';
    }

    return 'DEFAULT';
  }, [
    orgHasEdrProducts,
    orgHasEdrReports,
    orgHasVmpProducts,
    orgHasVmpReports,
    selectedOrganization,
    user
  ]);

  const { classes } = useStyles({
    availableReportTypes
  });

  const reportQuickFilters = useMemo(() => {
    const selectedReports =
      selectedOrganization.label === 'all' ? allReports : reports;
    const permissionOnlyReports = selectedReports.filter((report) => {
      switch (report.report_type_id) {
        case 1:
          return checkPermissions(
            [PERMISSIONS.MDR_READ],
            user,
            selectedOrganization
          );
        case 2:
          return checkPermissions(
            [PERMISSIONS.MDR_READ],
            user,
            selectedOrganization
          );
        case 3:
          return (
            orgHasEdrReports ||
            checkPermissions([PERMISSIONS.VMP_READ], user, selectedOrganization)
          );
        case 4:
          return checkPermissions(
            [PERMISSIONS.MDR_READ],
            user,
            selectedOrganization
          );
        default:
          return false;
      }
    });

    const qFilters = REPORT_QUICK_FILTERS.map((filter) => {
      const filterOptions = getSearchableSelectOptions(
        permissionOnlyReports,
        'report_frequency_name',
        'report_frequency_name'
      );

      return {
        ...filter,
        config: {
          ...filter.config,
          variant: filter.config.variant
        },
        filterOptions
      };
    });

    if (!qFilters[0]?.filterOptions[0]?.value) return qFilters; // Prevent sorting if no filter options

    const orderedFilterOptions = [] as SearchableSelectOption[];

    // Sort filter options in the order of Daily, Weekly, Monthly instead of alphabetically, only show filter buttons if their count is greater than 0
    if (qFilters[0].filterOptions?.find((option) => option.value === 'Daily')) {
      orderedFilterOptions.push(
        qFilters[0].filterOptions?.find(
          (option) => option.value === 'Daily'
        ) as SearchableSelectOption
      );
    }
    if (
      qFilters[0].filterOptions?.find((option) => option.value === 'Weekly')
    ) {
      orderedFilterOptions.push(
        qFilters[0].filterOptions?.find(
          (option) => option.value === 'Weekly'
        ) as SearchableSelectOption
      );
    }
    if (
      qFilters[0].filterOptions?.find((option) => option.value === 'Monthly')
    ) {
      orderedFilterOptions.push(
        qFilters[0].filterOptions?.find(
          (option) => option.value === 'Monthly'
        ) as SearchableSelectOption
      );
    }
    if (
      qFilters[0].filterOptions?.find((option) => option.value === 'Custom')
    ) {
      orderedFilterOptions.push(
        qFilters[0].filterOptions?.find(
          (option) => option.value === 'Custom'
        ) as SearchableSelectOption
      );
    }

    if (orderedFilterOptions.length === 1) {
      qFilters[0].filterOptions = [];
      return qFilters;
    }

    qFilters[0].filterOptions =
      orderedFilterOptions as SearchableSelectOption[];

    return qFilters;
  }, [filteredReports, selectedOrganization, userIsInternal]);

  const reportPermissionsCheck = (permission: string) => {
    return checkPermissions(permission, user, selectedOrganization);
  };

  const { ConfirmationModal: ReportDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Report?',
      message: 'Please confirm that you want to delete this report.',
      button: 'Delete'
    });

  const handleDelete = async (id: number) => {
    const confirmed = await confirmDelete();
    if (confirmed) {
      deleteReport(id);
    }
  };

  const headerChild = () => {
    if (!userIsInternal && selectedOrganization.label === 'all') {
      return null;
    }
    return (
      <Box sx={{ px: 3, pb: 2 }}>
        <SearchBar
          className="px-8 pb-4"
          searchableSelectFilters={reportQuickFilters}
          selectedFilters={reportsFilters}
          onFilterChange={setReportsFilters}
        />
      </Box>
    );
  };

  const handlePreviewClick = () => {
    const today = dayjs();
    // If today is before the 15th, show the previous month's report
    // If today is after the 15th, show the current month's report
    const start =
      today.date() < 15
        ? today.subtract(1, 'month').startOf('month')
        : today.startOf('month');
    const end = start.isSame(today.subtract(1, 'month').startOf('month'), 'day')
      ? start.endOf('month')
      : today;
    window.open(
      `/mdr-reports-template?token=valid_token&organization=${selectedOrganization.label}&start=${start.format(
        YEAR_MONTH_DAY_FORMAT
      )}&end=${end.format(YEAR_MONTH_DAY_FORMAT)}`,
      '_blank'
    );
  };

  const actionsBtns = useMemo(() => {
    return (
      <>
        {selectedOrganization.label !== 'all' && (
          <CheckPermissions permissions={[PERMISSIONS.REPORT_CREATE]}>
            <Button onClick={() => setOpen(true)} variant="contained">
              Upload Report
            </Button>
          </CheckPermissions>
        )}
        <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={DownloadIcon} />}
            onClick={() => setSocDialogOpen(true)}
          >
            SOC Monthly Summary
          </Button>
        </CheckPermissions>
        {selectedOrganization.label !== 'all' && (
          <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faEye} />}
              onClick={handlePreviewClick}
            >
              Preview Report
            </Button>
          </CheckPermissions>
        )}
      </>
    );
  }, [selectedOrganization]);

  return (
    <>
      <Header title="Reports" actions={actionsBtns}>
        {headerChild()}
      </Header>
      <MainContent>
        <div className={classes.reportsGrid}>
          <div className={classes.reportsGridSection}>
            <TableWidget
              title="Executive Reports"
              columns={createColumns(
                setOpenTitleEdit,
                handleDelete,
                reportPermissionsCheck,
                setSelectedReport,
                organizations,
                selectedOrganization.label,
                organizationIdNames
              )}
              rows={executiveReports}
              loading={false}
              cellAlignment="center"
            />
          </div>
          <div className={classes.reportsGridSection}>
            <TableWidget
              title="MDR Reports"
              columns={createColumns(
                setOpenTitleEdit,
                handleDelete,
                reportPermissionsCheck,
                setSelectedReport,
                organizations,
                selectedOrganization.label,
                organizationIdNames
              )}
              rows={mdrReports}
              loading={false}
              cellAlignment="center"
            />
          </div>
          {showEdr && (
            <div className={classes.reportsGridSection}>
              <TableWidget
                title="EDR Reports"
                columns={createColumns(
                  setOpenTitleEdit,
                  handleDelete,
                  reportPermissionsCheck,
                  setSelectedReport,
                  organizations,
                  selectedOrganization.label,
                  organizationIdNames
                )}
                rows={edrReports}
                loading={false}
                cellAlignment="center"
              />
            </div>
          )}
          {showVmp && (
            <div className={classes.reportsGridSection}>
              <TableWidget
                title="Vulnerability Reports"
                columns={createColumns(
                  setOpenTitleEdit,
                  handleDelete,
                  reportPermissionsCheck,
                  setSelectedReport,
                  organizations,
                  selectedOrganization.label,
                  organizationIdNames
                )}
                rows={vmpReports}
                loading={false}
                cellAlignment="center"
              />
            </div>
          )}
        </div>
      </MainContent>
      {open && <ReportsDialog open={open} setOpen={setOpen} />}
      {socDialogOpen && (
        <SocMonthlySummaryDialog
          open={socDialogOpen}
          setOpen={setSocDialogOpen}
        />
      )}
      {openTitleEdit && (
        <ReportTitleEditDialog
          key={selectedReport?.id}
          selectedReport={selectedReport}
          open={openTitleEdit}
          setOpen={setOpenTitleEdit}
        />
      )}
      <ReportDeleteModal />
    </>
  );
};

export default Reports;
